// Runs a background refresh without letting requests for it pile up.
//
// The client's program screen re-reads its completion data from several places: a timer, coming
// back to the app, and every saved set. Each read is three queries. Fired independently they
// overlap, and when the database is slow they queue up behind each other and make it slower. This
// keeps it to one refresh at a time:
//
//   * a request waits `delayMs` first, so a burst (weight, then reps, a second apart) becomes one;
//   * while a refresh is running, further requests are remembered as ONE follow-up, not many;
//   * the refresh always uses the arguments of the most recent request.
//
// A refresh that throws is swallowed: it is a background read, and the next one will try again.

export const createCoalescedRefresher = (refresh, { delayMs = 0 } = {}) => {
  let timer = null;
  let running = false;
  let pendingArgs = null; // the latest request not yet started
  let cancelled = false;

  const start = async () => {
    timer = null;
    if (cancelled || !pendingArgs) return;
    const args = pendingArgs;
    pendingArgs = null;
    running = true;
    try {
      await refresh(...args);
    } catch {
      // background read: the next request tries again
    } finally {
      running = false;
      // Requests that arrived during the run: one follow-up, with the latest arguments.
      if (pendingArgs) timer = setTimeout(start, delayMs);
    }
  };

  return {
    /** Ask for a refresh. Returns straight away. */
    request: (...args) => {
      pendingArgs = args;
      // Already scheduled, or a run is in flight and will pick this up when it ends. Not
      // rescheduling is what stops a steady stream of requests from postponing it for ever.
      if (running || timer) return;
      timer = setTimeout(start, delayMs);
    },
    /** Drop anything not yet started and ignore further requests (for unmount). */
    cancel: () => {
      cancelled = true;
      pendingArgs = null;
      clearTimeout(timer);
      timer = null;
    },
  };
};
